import { z } from 'zod';
import { notificationStatsSchema, pushNotificationSchema, } from './notifications.js';
import { accessTokenSchema, accountInteractionsSchema, accountNameSchema, accountSchema, answerSchema, applicationDataSchema, groupDataSchema, groupRoleSchema, groupSchema, guestDataSchema, hashtagSchema, inviteLinkSchema, jamRecordingStatusSchema, latLngSchema, locationSchema, mediaAttachmentSchema, mentionDataSchema, postDataUnionSchema, postTypeSchema, profileDataSchema, profileStatsSchema, reactionDataSchema, roleDataSchema, reportDataSchema, reportSchema, resourceSchema, rsvpSchema, scopeSchema, visibilityTypeSchema, } from './models.js';
import { groupWithMetaSchema } from './internal.js';
import { communityConfigSchema } from './config.js';
import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi';
extendZodWithOpenApi(z);
export const errorResponseBodySchema = z.object({
    success: z.literal(false),
    message: z.string(),
});
export const publicAccountSchema = z
    .object({
    id: z.uuid(),
    email: z.email(),
    emailValidated: z.boolean().default(false),
})
    .openapi('Account', { type: 'object' });
/** Role identity on public profiles — capabilities stay internal. */
export const publicRoleSchema = roleDataSchema.pick({
    key: true,
    displayName: true,
});
export const publicProfileSchema = z.object({
    id: z.string().uuid(),
    type: z.enum(['local', 'guest', 'federated']),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    deletedAt: z.string().datetime().nullable().optional(),
    handle: accountNameSchema,
    avatar: z.string().url().nullable().optional(),
    header: z.string().url().nullable().optional(),
    displayName: z.string().max(30).optional(),
    bio: z.string().optional(),
    location: locationSchema.optional(),
    locked: z.boolean().optional(),
    bot: z.boolean().optional(),
    discoverable: z.boolean().optional(),
    roles: publicRoleSchema.array().optional(),
    memberships: z
        .array(groupRoleSchema.extend({
        group: groupSchema,
    }))
        .optional()
        .transform((memberships) => memberships?.filter((m) => m.group.capabilities?.none?.add?.includes('core-groups-read'))),
    fields: z
        .array(z.object({
        name: z.string().max(255).min(1),
        value: z.string().max(255),
    }))
        .max(4)
        .optional(),
    profileStats: profileStatsSchema.optional(),
    pinnedPostId: z.string().optional(),
    blockedByMe: z.boolean().optional(),
});
export const publicAccessTokenSchema = accessTokenSchema
    .omit({ signedToken: true })
    .extend({
    ownedBy: publicProfileSchema.optional(),
    scopes: z.array(scopeSchema).optional(),
})
    .openapi('PublicAccessToken');
export const jamParticipantProfileSchema = publicProfileSchema.extend(guestDataSchema.partial().shape);
export const mentionWithPublicProfileSchema = mentionDataSchema.extend({
    profile: publicProfileSchema,
});
export const entryWithPublicProfileSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('create'),
        data: postDataUnionSchema,
        profile: publicProfileSchema,
        createdAt: z.string().datetime(),
    }),
    z.object({
        type: z.literal('edit'),
        data: postDataUnionSchema,
        profile: publicProfileSchema,
        createdAt: z.string().datetime(),
    }),
    z.object({
        type: z.literal('delete'),
        data: z.any(),
        profile: publicProfileSchema,
        createdAt: z.string().datetime(),
    }),
    z.object({
        type: z.literal('answer'),
        data: answerSchema,
        profile: publicProfileSchema,
        createdAt: z.string().datetime(),
    }),
    z.object({
        type: z.literal('rsvp'),
        data: rsvpSchema,
        profile: publicProfileSchema,
        createdAt: z.string().datetime(),
    }),
]);
const reactionWithPublicProfileSchema = reactionDataSchema.extend({
    profile: publicProfileSchema,
});
const repostWithPublicProfileSchema = z.object({
    profile: publicProfileSchema,
    createdAt: z.string().datetime(),
});
const publicReplyPostObject = z.object({
    id: z.string(),
    type: postTypeSchema,
    profile: publicProfileSchema,
    data: postDataUnionSchema,
    entries: entryWithPublicProfileSchema.array(),
    createdAt: z.string().datetime(),
    deletedAt: z.string().datetime().optional().nullable(),
    visibility: visibilityTypeSchema,
    group: groupWithMetaSchema.optional().nullable(),
    audience: z.array(publicProfileSchema).optional().nullable(),
    seen: z.boolean().optional(),
    inReplyToId: z.string().optional().nullable(),
    hidden: z.boolean().optional(),
});
export const publicReplyPostSchema = publicReplyPostObject.extend({
    replyTo: publicReplyPostObject.optional().nullable(),
});
export const publicApplicationSchema = applicationDataSchema.pick({
    name: true,
    website: true,
});
const publicRsvpSchema = rsvpSchema.extend({
    profile: publicProfileSchema,
    createdAt: z.string().datetime(),
});
const basePublicPostSchema = publicReplyPostSchema.extend({
    repostCount: z.number(),
    replyCount: z.number(),
    reactions: reactionWithPublicProfileSchema.array(),
    /** Capped lean list of who reposted (most recent). Count stays in repostCount. */
    reposts: repostWithPublicProfileSchema.array(),
    /** Newest conversation descendants for a nested timeline preview. */
    latestReplies: publicReplyPostSchema.array().optional(),
    latestRepliesHasMore: z.boolean().optional(),
    /** Last reply on the conversation root; feeds sort by this. */
    lastActivityAt: z.string().datetime().optional(),
    application: publicApplicationSchema.optional(),
    mentions: z.array(mentionWithPublicProfileSchema),
    tags: z.array(hashtagSchema),
    groupId: z.string().optional().nullable(),
    groupDiscoverable: z.boolean().optional().nullable(),
    updatedAt: z.string().datetime(),
    accountInteractions: accountInteractionsSchema.optional(),
    rsvps: publicRsvpSchema.array(),
    seen: z.boolean().optional(),
    occurrenceRecurrenceId: z.string().datetime().optional(),
    occurrenceStart: z.string().datetime().optional(),
    occurrenceEnd: z.string().datetime().optional(),
});
const withReplyPostSchema = basePublicPostSchema.extend({
    inReplyToId: z.string().optional().nullable(),
    replyTo: publicReplyPostSchema.optional().nullable(),
});
export const publicPostSchema = withReplyPostSchema
    .extend({
    repost: basePublicPostSchema.optional().nullable(),
})
    .openapi('Post');
export const publicJamEventSchema = z.object({
    jamId: z.string(),
    type: z.enum(['message', 'reaction', 'attendance', 'active']),
    content: z.string(),
    sender: z.object({
        profile: publicProfileSchema.extend({
            email: z.string().email().optional(),
        }),
        participantId: z.string(),
    }),
    joinTime: z.string().datetime().optional(),
    leaveTime: z.string().datetime().optional(),
});
export const publicNotificationSchema = z.object({
    id: z.string().uuid(),
    type: z.string(),
    seen: z.boolean().optional(),
    read: z.boolean().optional(),
    createdAt: z.string().datetime(),
    senderProfile: publicProfileSchema.nullable().optional(),
    group: groupSchema.nullable().optional(),
    post: publicPostSchema.nullable().optional(),
    data: z.any().optional(),
});
export const publicGroupMemberSchema = groupRoleSchema.extend({
    profile: publicProfileSchema,
});
export const accountCreationDataSchema = z.object({
    email: z.string().email(),
    password: z.string().min(8),
    emailValidated: z.boolean().optional(),
    inviteCode: z.string().optional(),
    profile: z.object({
        handle: z.string().min(4).max(255),
        avatar: z.string().url().optional(),
        displayName: profileDataSchema.shape.displayName,
        bot: profileDataSchema.shape.bot,
    }),
});
export const accountUpdateDataSchema = z.object({
    account: accountSchema,
    email: z.string().email().optional(),
    password: z.string().min(8).optional(),
    emailValidated: z.boolean().optional(),
});
export const serverInfoSchema = z.object({
    version: z.string(),
    /** Optional CI/image build identifier (`BUILD`). */
    build: z.string().optional(),
    environment: z.string(),
    publicContent: z.boolean(),
    /** Process start time (ISO). */
    startedAt: z.iso.datetime(),
    uptimeSeconds: z.number().nonnegative(),
    /** Local media-folder size and volume free space; null when unreadable. */
    disk: z
        .object({
        folderBytes: z.number().nonnegative(),
        freeBytes: z.number().nonnegative(),
        totalBytes: z.number().nonnegative(),
    })
        .nullable(),
    /** Latest post view (`post_seen.created_at`); null if none yet. */
    lastAccessed: z.iso.datetime().nullable(),
    maxProfiles: z.number().int().nonnegative().optional(),
    communityConfig: communityConfigSchema,
    jams: z.object({
        livekit: z.object({
            url: z.string(),
            enabled: z.boolean(),
            recordingEnabled: z.boolean(),
        }),
    }),
    vapid: z.object({
        publicKey: z.string().optional(),
    }),
    sentryConfig: z.object({
        enabled: z.boolean(),
        dsn: z.string().optional(),
        replayEnabled: z.boolean().optional(),
    }),
    payments: z.object({
        stripe: z.object({
            paidMembership: z.object({
                enabled: z.boolean(),
            }),
        }),
    }),
    sso: z
        .object({
        onlySSO: z.boolean().optional(),
        generic: z.array(z.object({
            id: z.string(),
            name: z.string(),
            loginLink: z.string(),
        })),
        oidc: z.array(z.object({
            id: z.string(),
            name: z.string(),
        })),
    })
        .optional(),
});
export const unseenPostCountsSchema = z.object({
    groups: z.record(z.string(), z.number()),
    direct: z.record(z.string(), z.number()),
});
export const pushPayloadSchema = z.object({
    notification: pushNotificationSchema,
    notificationStats: notificationStatsSchema,
});
export const webhookDataSchema = z.object({
    type: z.literal('pushNotification'),
    payload: pushPayloadSchema,
});
export const webhookKeyResponseSchema = z.object({
    publicKey: z.string(),
});
export const webhookVerifyRequestSchema = z.object({
    token: z.string(),
});
export const webhookVerifyResponseSchema = z.object({
    success: z.literal(true),
    payload: z.record(z.string(), z.unknown()),
});
export const publicInviteLinkSchema = inviteLinkSchema.extend({
    profile: publicProfileSchema,
    redemptions: publicProfileSchema.array(),
});
export const muteParticipantRequestSchema = z.object({
    identity: z.string().uuid(),
    trackSid: z.string(),
});
export const guestPassRequestSchema = z.object({
    displayName: z.string().max(30),
    email: z.string().email(),
    resource: resourceSchema,
});
export const logRowSchema = z.object({
    level: z.string(),
    message: z.string(),
    timestamp: z.string().date(),
    meta: z.any(),
    namespace: z.string(),
});
export const successResponseSchema = z.object({
    success: z.literal(true),
});
export const successFailureResponseSchema = z.object({
    success: z.boolean(),
    message: z.string().optional(),
    key: z.string().optional(),
});
export const fetchUrlRequestSchema = z.object({
    url: z.url(),
});
export const fetchUrlReponseSchema = z.object({
    statusCode: z.number(),
    success: z.boolean(),
    data: z.object({
        image: z.string(),
        title: z.string(),
        description: z.string(),
        url: z.string(),
    }),
});
export const loginRequestSchema = z.object({
    email: z.email(),
    password: z.string().min(8),
});
export const registerRequestSchema = z.object({
    handle: accountNameSchema,
    email: z.email(),
    password: z
        .string()
        .min(8, { message: 'Your password should be at least 8 characters long' }),
    displayName: profileDataSchema.shape.displayName,
    confirmPassword: z.string().optional(),
    privacyPolicyAccepted: z.boolean(),
    inviteCode: z.string().optional(),
    bot: profileDataSchema.shape.bot,
});
export const registerFormSchema = registerRequestSchema
    .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
})
    .refine((data) => data.privacyPolicyAccepted, {
    message: 'You must agree to the privacy policy',
    path: ['privacyPolicyAccepted'],
});
export const createGroupRequestSchema = groupDataSchema.extend({
    members: publicProfileSchema.array().optional(),
});
export const requestResetPasswordRequestSchema = z.object({
    email: z.string().email(),
});
export const resetPasswordRequestSchema = z.object({
    password: z.string().min(8),
    confirmPassword: z.string().optional(),
});
export const resetPasswordFormSchema = resetPasswordRequestSchema.refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
});
export const validateEmailQuerySchema = z.object({
    token: z.string().min(1),
});
export const tokenResponseSchema = z.object({
    success: z.literal(true),
    token: z.string(),
    checkoutUrl: z.string().optional(),
});
export const jamTokenResponseSchema = tokenResponseSchema.extend({
    livekitUrl: z.string(),
});
const focusCoordinatesPattern = /^-?\d+,-?\d+$/;
// NOTE: `file` / `thumbnail` accept a `File` (browser) or any
// multipart body — modelled as `z.any()` for the API contract so the OpenAPI
// generator can produce a binary content schema. Handlers should runtime-check
// they actually got a `File` / `Blob` instance.
export const mediaStorageRequestSchema = z.object({
    description: z.string().optional(),
    file: z.any().openapi({
        type: 'string',
        format: 'binary',
    }),
    thumbnail: z
        .any()
        .optional()
        .openapi({ type: 'string', format: 'binary' }),
    usage: z.string(),
    focus: z
        .string()
        .regex(focusCoordinatesPattern, 'focus must be "<integer>,<integer>"')
        .optional(),
});
export const mediaProgressEventSchema = z
    .object({
    mediaAttachment: mediaAttachmentSchema,
    progressPercent: z.number().min(0).max(100),
    estimatedRemainingMs: z.number().optional(),
})
    .openapi('MediaProgressEvent');
export const mediaStreamStatusSchema = z
    .enum(['processing', 'ready', 'error'])
    .openapi('MediaStreamStatus');
export const mediaStreamSchema = z
    .object({
    storageId: z.string(),
    status: mediaStreamStatusSchema,
    error: z.string().optional(),
})
    .openapi('MediaStream');
export const mediaStreamRequestSchema = z
    .object({
    url: z.string().url(),
})
    .openapi('MediaStreamRequest');
export const updateProfileRequestSchema = profileDataSchema
    .omit({ pinnedPostId: true })
    .partial({
    handle: true,
})
    .superRefine((value, ctx) => {
    if (value.handle === undefined || value.type === 'federated')
        return;
    if (!accountNameSchema.safeParse(value.handle).success) {
        ctx.addIssue({
            code: 'custom',
            path: ['handle'],
            message: 'Handle must be 1–16 characters: letters, numbers, underscores, or hyphens (no spaces or punctuation)',
        });
    }
});
export const updateAccountPasswordRequestSchema = z.object({
    email: z.string().email().optional(),
    newPassword: z.preprocess((s) => s || undefined, z.string().min(8).optional()),
    oldPassword: z.string().min(8),
    confirmPassword: z.preprocess((s) => s || undefined, z.string().min(8).optional()),
});
export const updateAccountPasswordFormSchema = updateAccountPasswordRequestSchema
    .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
})
    .refine((data) => data.oldPassword, {
    message: 'You must provide your old password to change your password or email',
    path: ['__form__'],
});
export const updateAccountEmailRequestSchema = z.object({
    newEmail: z.email(),
    oldEmail: z.email(),
});
export const geocodingResultSchema = z.object({
    name: z.string(),
    bbox: latLngSchema.array().length(2),
    center: latLngSchema,
    icon: z.string().optional(),
    properties: z.record(z.string(), z.any()).optional(),
});
export const reportCreationDataSchema = z.object({
    report: reportDataSchema,
    profileId: z.string(),
    postIds: z.string().array(),
});
export const jamStateSchema = z.object({
    active: z.boolean(),
    participants: z.uuid().array(),
});
export const publicReportSchema = reportSchema.extend({
    reporterProfile: publicProfileSchema,
    reportedProfile: publicProfileSchema,
    reportedPosts: z.array(publicPostSchema),
});
export const chronologicalInfiniteQueryParamsSchema = z.object({
    start: z.string().optional(),
    limit: z.coerce.number().optional(),
    format: z.enum(['threaded', 'linear']).optional(),
    groupId: z.string().optional(),
    /** Query-string flag; fetch params only allow string | number. */
    ungrouped: z.literal('true').optional(),
});
export const offsetInfiniteQueryParamsSchema = z.object({
    offset: z.coerce.number().optional(),
    limit: z.coerce.number().optional(),
});
export const searchResultItemSchema = (baseSchema) => z.object({
    score: z.number(),
    data: baseSchema,
});
export const searchResultSchema = (baseSchema) => searchResultItemSchema(baseSchema).array();
export const searchResultCountsSchema = z.object({
    groups: z.number(),
    profiles: z.number(),
    posts: z.number(),
    events: z.number(),
    jams: z.number(),
});
// Plain object schema — used as API contract & in OpenAPI generation. Cross-
// field validation lives on `postCreationDataFormSchema` below to keep this
// schema introspectable by zod-to-openapi (which can't walk zod-4 `.refine()`
// wrappers, as they appear as a `ZodCustom` with `def.type === 'custom'`).
export const postCreationDataSchema = z.object({
    visibility: visibilityTypeSchema,
    type: postTypeSchema,
    data: postDataUnionSchema,
    inReplyToId: z.uuid().nullable().optional(),
    mentions: mentionWithPublicProfileSchema.array().nullable().optional(),
    audience: publicProfileSchema.array().nullable().optional(),
    groupId: z.uuid().nullable().optional(),
    reportId: z.uuid().nullable().optional(),
    capabilitiesNeeded: z.string().array().nullable().optional(),
});
export const postCreationDataFormSchema = postCreationDataSchema
    .refine((data) => {
    if (data.type !== 'event')
        return true;
    if (data.data.type !== 'event')
        return true;
    return !!data.data.name;
}, {
    message: 'Name is required',
    path: ['data', 'name'],
})
    .refine((data) => {
    if (data.type !== 'event')
        return true;
    if (data.data.type !== 'event')
        return true;
    const { start, end } = data.data;
    if (end && start && new Date(end) < new Date(start)) {
        return false;
    }
    return true;
}, {
    message: 'End date cannot be before start date',
    path: ['data', 'end'],
});
const answerWithPublicProfileSchema = answerSchema.extend({
    profile: publicProfileSchema,
});
export const paymentCheckoutSchema = successFailureResponseSchema.extend({
    url: z.url().nullable(),
});
export const jamObserverResponseSchema = z.object({
    path: z.string().optional(),
});
export const jamRtmpStreamRequestSchema = z.object({
    url: z.string().min(1),
    streamKey: z.string().min(1),
});
export const jamRtmpStreamResponseSchema = z.object({
    id: z.string(),
    status: jamRecordingStatusSchema,
    destinationHost: z.string().optional(),
    egressId: z.string().optional(),
});
export const accessTokenCreationDataSchema = z.object({
    name: z.string(),
    description: z.string().optional(),
    scopes: z.array(scopeSchema).optional(),
    expirationTime: z.string(),
});
